/*
 * Gráfica de barras horizontales apiladas, réplica de Chart.js (stacked) del HTML:
 * leyenda clickable que oculta segmentos, ejes, animación, hover y tooltip.
 */
import { useEffect, useRef, useState } from "react";

import Tooltipx from "./Tooltipx";
import { bucketColors, buildTicks, fmtCOP, htmlTheme } from "./utils/carthelp";

const AXIS_H = 22;
const LEGEND_H = 26;
const LABEL_W = 170;
const SEGMENTS = ["verde", "amarillo", "rojo"];

/**
 * Renderiza una gráfica de barras apiladas con leyenda, ejes y gridlines
 * @param {Object} props Propiedades del componente
 * @param {Array} props.data Filas [{ label, raw, verde, amarillo, rojo, total }]
 * @param {Function} props.onItemClick Callback con la fila y su índice al hacer click
 * @param {Array} props.legend Etiquetas de la leyenda [verde, amarillo, rojo]
 * @param {number} props.height Alto total del área de la gráfica
 */
const Stakbars = ({ data = [], onItemClick, legend = ["Verde", "Amarillo", "Rojo"], height = 400 }) => {
  const bClickable = typeof onItemClick === "function";
  const nRowH = (height - AXIS_H - LEGEND_H) / Math.max(1, data.length);
  const nBarH = Math.min(nRowH * 0.72, 26);

  const containerRef = useRef(null);
  const [bMounted, setMounted] = useState(false);
  const [nHover, setHover] = useState(null);
  const [mTip, setTip] = useState(null);
  const [mHidden, setHidden] = useState([]);

  //Segmentos visibles y escala recalculada (como al ocultar datasets en Chart.js)
  const mVisible = SEGMENTS.filter((sSeg) => !mHidden.includes(sSeg));
  const visibleTotal = (mItem) => mVisible.reduce((a, sSeg) => a + mItem[sSeg], 0);
  const { ticks, top } = buildTicks(Math.max(1, ...data.map((mItem) => visibleTotal(mItem))));

  //Dispara la animación de entrada de las barras
  useEffect(() => {
    const nTimer = setTimeout(() => setMounted(true), 30);
    return () => clearTimeout(nTimer);
  }, []);

  //Oculta o muestra un segmento desde la leyenda
  const toggleSegment = (sSeg) => {
    setHidden((mPrev) => (mPrev.includes(sSeg) ? mPrev.filter((sS) => sS !== sSeg) : [...mPrev, sSeg]));
  };

  //Actualiza la posición del tooltip siguiendo el mouse
  const handleMove = (oEvent, mItem, sSeg) => {
    const oRect = containerRef.current?.getBoundingClientRect();
    if (!oRect) return;
    const nSegIndex = SEGMENTS.indexOf(sSeg);
    setTip({
      x: oEvent.clientX - oRect.left,
      y: oEvent.clientY - oRect.top,
      title: mItem.raw || mItem.label,
      lines: [`${legend[nSegIndex]}: ${fmtCOP(mItem[sSeg])}`],
    });
  };

  //Limpia el estado de hover y tooltip
  const handleLeave = () => {
    setHover(null);
    setTip(null);
  };

  return (
    <div ref={containerRef} style={{ height, position: "relative" }} onMouseLeave={handleLeave}>
      <Tooltipx tip={mTip} />
      <div style={{ height: LEGEND_H, display: "flex", justifyContent: "center", gap: 18, alignItems: "center" }}>
        {SEGMENTS.map((sSeg, nIndex) => {
          const bHidden = mHidden.includes(sSeg);
          return (
            <span
              key={sSeg}
              onClick={() => toggleSegment(sSeg)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 11,
                cursor: "pointer",
                userSelect: "none",
                color: bHidden ? htmlTheme.muted : htmlTheme.ink,
                textDecoration: bHidden ? "line-through" : "none",
              }}
            >
              <span style={{ width: 12, height: 12, background: bucketColors[sSeg], display: "inline-block", opacity: bHidden ? 0.35 : 1 }} />
              {legend[nIndex]}
            </span>
          );
        })}
      </div>
      <div style={{ display: "flex", height: height - LEGEND_H }}>
        <div style={{ width: LABEL_W, flexShrink: 0 }}>
          {data.map((mItem, nIndex) => (
            <div
              key={nIndex}
              title={mItem.raw || mItem.label}
              style={{
                height: nRowH,
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: 8,
                fontSize: 11,
                color: htmlTheme.muted,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
              }}
            >
              {mItem.label}
            </div>
          ))}
        </div>
        <div style={{ flex: 1, position: "relative" }}>
          {ticks.map((nTick) => (
            <div
              key={nTick}
              style={{
                position: "absolute",
                left: `${(nTick / top) * 100}%`,
                top: 0,
                bottom: AXIS_H,
                borderLeft: `1px solid ${nTick === 0 ? "#D5DAE8" : "#EDEFF7"}`,
              }}
            />
          ))}
          {data.map((mItem, nIndex) => {
            const nTotal = visibleTotal(mItem);
            return (
              <div key={nIndex} style={{ height: nRowH, display: "flex", alignItems: "center" }}>
                <div
                  onClick={bClickable ? () => onItemClick(mItem, nIndex) : undefined}
                  onMouseEnter={() => setHover(nIndex)}
                  style={{
                    display: "flex",
                    width: bMounted ? `${(nTotal / top) * 100}%` : 0,
                    height: nBarH,
                    minWidth: bMounted && nTotal > 0 ? 2 : 0,
                    cursor: bClickable ? "pointer" : "default",
                    transition: "width .5s ease, filter .15s ease",
                    filter: nHover === nIndex ? "brightness(1.12)" : "none",
                  }}
                >
                  {mVisible.map((sSeg) =>
                    mItem[sSeg] > 0 && nTotal > 0 ? (
                      <div
                        key={sSeg}
                        onMouseMove={(oEvent) => handleMove(oEvent, mItem, sSeg)}
                        style={{
                          width: `${(mItem[sSeg] / nTotal) * 100}%`,
                          background: bucketColors[sSeg],
                          transition: "width .5s ease",
                        }}
                      />
                    ) : null,
                  )}
                </div>
              </div>
            );
          })}
          <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: AXIS_H }}>
            {ticks.map((nTick) => (
              <span
                key={nTick}
                style={{
                  position: "absolute",
                  left: `${(nTick / top) * 100}%`,
                  transform: "translateX(-50%)",
                  fontSize: 10,
                  color: htmlTheme.muted,
                  whiteSpace: "nowrap",
                }}
              >
                {fmtCOP(nTick)}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stakbars;
